import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllEmployees, resetEmployees } from "../../slices/employeeSlice";
import { fetchAllCompanies } from "../../slices/companySlice";
import {
  downloadQuestionnaireReport,
  questionnaireReportData,
  reset,
} from "../../slices/questionnaireReportSlice";
import moment from "moment";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormControl, InputLabel, Select, MenuItem, Box } from "@mui/material";
import CButton from "../../components/CButton";
import { DataGrid } from "@mui/x-data-grid";
import CTextField from "../../components/CTextfield";

const QuestionnaireReport = () => {
  const dispatch = useDispatch();
  const { employeeList } = useSelector((state) => state.employee);
  const { companyList } = useSelector((state) => state.company);
  const { totalRows, questionnaireData } = useSelector(
    (state) => state.questionReport
  );

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [error, setError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [totalRowsData, setTotalRowsData] = useState(0);
  const [reportsData, setReportsData] = useState([]);
  const [idNo, setIdNo] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  useEffect(() => {
    const fetchAllEmployeeAPI = () => {
      let payload = {
        pagesData: {
          page: 1,
          limit: -1,
        },
        companyId: selectedCompany,
      };
      dispatch(fetchAllEmployees(payload));
    };
    selectedCompany && fetchAllEmployeeAPI();
  }, [selectedCompany, dispatch]);

  useEffect(() => {
    const fetchAllCompaniesAPI = () => {
      let payload = {
        page: 1,
        limit: -1,
      };
      dispatch(fetchAllCompanies(payload));
    };
    fetchAllCompaniesAPI();
  }, [dispatch]);

  useEffect(() => {
    setTotalRowsData(totalRows);
  }, [totalRows]);

  useEffect(() => {
    setReportsData(questionnaireData);
  }, [questionnaireData]);

  useEffect(() => {
    if (employeeList.length > 0) setSelectedEmployee("All");
  }, [employeeList]);

  useEffect(() => {
    dispatch(reset());
    dispatch(resetEmployees());
  }, [dispatch]);

  useEffect(() => {
    const fetchQuestionDetails = () => {
      let payload = {
        pageData: {
          ...(idNo && { idNo: String(idNo) }),
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        },
        employeeData: {
          ...(selectedEmployee !== "All" && { employeeId: selectedEmployee }),
          employerId: selectedCompany,
          startDate: fromDate?.format("DD/MM/YYYY"),
          endDate: toDate?.format("DD/MM/YYYY"),
        },
      };
      dispatch(questionnaireReportData(payload));
    };

    if (
      (toDate && fromDate && selectedCompany && selectedEmployee) ||
      (selectedCompany && selectedEmployee && !toDate && !fromDate)
    ) {
      fetchQuestionDetails();
    }

    if (!moment(fromDate).isValid() || !moment(toDate).isValid()) {
      setError("Please fill out necessary details");
    } else if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      toast.error("Invalid Date Range");
      setError("Invalid Date Range");
    } else if (!selectedEmployee) {
      setError("Please fill out necessary details");
    } else {
      setError("");
    }
  }, [fromDate, toDate, selectedEmployee,idNo, paginationModel, dispatch]);

  const handleDownload = (resUrl) => {
    if (resUrl) {
      const url =
        resUrl.startsWith("http") || resUrl.startsWith("https")
          ? resUrl
          : `http://${resUrl}`;

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const columns = [
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1.5,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "lmaContactNo",
      headerName: "Cell Number",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "idNo",
      headerName: "Id No.",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "empNo",
      headerName: "Employee No.",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "employerName",
      headerName: "Employer Name",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "dateSubmitted",
      headerName: "Date Submitted",
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: "question1",
      headerName: "Response 1",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[0]?.answer || "—"}</span>
      ),
    },
    {
      field: "question2",
      headerName: "Response 2",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[1]?.answer || "—"}</span>
      ),
    },
    {
      field: "question3",
      headerName: "Response 3",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[2]?.answer || "—"}</span>
      ),
    },
    {
      field: "question4",
      headerName: "Response 4",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[3]?.answer || "—"}</span>
      ),
    },
    {
      field: "question5",
      headerName: "Response 5",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[4]?.answer || "—"}</span>
      ),
    },
    {
      field: "question6",
      headerName: "Response 6",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[5]?.answer || "—"}</span>
      ),
    },
    {
      field: "question7",
      headerName: "Response 7",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[6]?.answer || "—"}</span>
      ),
    },
    {
      field: "question8",
      headerName: "Response 8",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[7]?.answer || "—"}</span>
      ),
    },
    {
      field: "question9",
      headerName: "Response 9",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[8]?.answer || "—"}</span>
      ),
    },
    {
      field: "question10",
      headerName: "Response 10",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[9]?.answer || "—"}</span>
      ),
    },
    {
      field: "question11",
      headerName: "Response 11",
      flex: 1.5,
      renderCell: (params) => (
        <span>{params.row?.questionnaireActivity?.[10]?.answer || "—"}</span>
      ),
    },
  ];
  const handleSubmit = () => {
    if (!error) {
      let payload = {
        ...(selectedEmployee !== "All" && { employeeId: selectedEmployee }),
        employerId: selectedCompany,
        startDate: fromDate?.format("DD/MM/YYYY"),
        endDate: toDate?.format("DD/MM/YYYY"),
      };
      dispatch(downloadQuestionnaireReport(payload)).then((res) => {
        if (!res.error) {
          handleDownload(res.payload.data.url);
        }
      });
    } else {
      toast.error(error);
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };
  const Placeholder = ({ children }) => {
    return <div>{children}</div>;
  };

  return (
    <div className="main-content report-content">
      <h2>Questionnaire Report</h2>

      <div className="card-box">
        <div className="report-table-filter">
          <div className="report-table-left">
            <p className="date-range-label">Date Range</p>
            <div className="date-range-fields">
              <div className="CDatepicker">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={fromDate || null}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      setPaginationModel({
                        page: 0,
                        pageSize: 25,
                      });
                    }}
                  />
                </LocalizationProvider>
              </div>
              <p>to</p>
              <div className="CDatepicker">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={toDate || null}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      setPaginationModel({
                        page: 0,
                        pageSize: 25,
                      });
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
          <div className="report-table-right">
            <FormControl fullWidth className="CSelect">
              <InputLabel id="demo-simple-select-label">Employer</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                label="Employee"
                name="employee"
                id="demo-simple-select"
                displayEmpty
                renderValue={
                  selectedCompany !== null
                    ? undefined
                    : () => <Placeholder>Select Employer</Placeholder>
                }
                onChange={(e) => {
                  setSelectedEmployee(null);
                  setSelectedCompany(e.target.value);
                  setPaginationModel({
                    page: 0,
                    pageSize: 25,
                  });
                  dispatch(reset());
                }}
                placeholder="Select"
                value={selectedCompany || ""}
                IconComponent={(props) => <ExpandMoreIcon {...props} />}
                inputlabelprops={{ shrink: true }}
              >
                {companyList?.map((data) => (
                  <MenuItem key={data._id} value={data._id}>
                    {data?.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className="CSelect">
              <InputLabel id="demo-simple-select-label">Employee</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                label="Employee"
                name="employee"
                id="demo-simple-select"
                displayEmpty
                renderValue={
                  selectedEmployee !== null
                    ? undefined
                    : () => <Placeholder>Select Employee</Placeholder>
                }
                onChange={(e) => {
                  setSelectedEmployee(e.target.value);
                  setPaginationModel({
                    page: 0,
                    pageSize: 25,
                  });
                }}
                placeholder="Select"
                value={selectedEmployee || ""}
                disabled={!employeeList.length}
                IconComponent={(props) => <ExpandMoreIcon {...props} />}
                inputlabelprops={{ shrink: true }}
              >
                <MenuItem value="All">All</MenuItem>
                {employeeList?.map((data) => (
                  <MenuItem key={data._id} value={data._id}>
                    {data?.firstName + " " + data?.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <CTextField
              label={"ID number"}
              disabled={!selectedCompany}
              placeholder={"ID number"}
              value={idNo}
              type="number"
              handleChange={(e) => {
                setIdNo(e.target.value);
                setPaginationModel({
                  page: 0,
                  pageSize: 25,
                });
              }}
            />
            <CButton
              className="btn-primary"
              disabled={reportsData.length <= 0 || !!error}
              onClick={handleSubmit}
            >
              Export CSV
            </CButton>
          </div>
        </div>
        <Box className="table-box report_table">
          <DataGrid
            initialState={{
              columns: {
                columnVisibilityModel: {
                  _id: false,
                },
              },
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: {
                sortModel: [{ field: "dateSubmitted", sort: "asc" }],
              }
            }}
            rowCount={totalRowsData || 0}
            // pageSize={pageSize}
            // page={page}
            columns={columns}
            rows={reportsData}
            paginationModel={paginationModel}
            paginationMode="server"
            autoHeight={false}
            getRowId={(row) => row._id}
            rowSelection={false}
            // hideFooterPagination
            // hideFooter
            onPaginationModelChange={(e) => handlePaginationChange(e)}
            onChange
            //pagination={true}
            disableColumnFilter
            hover={false}
            localeText={{ noRowsLabel: "No records found" }}
          />
        </Box>
      </div>
    </div>
  );
};

export default QuestionnaireReport;
